// Pre-Approval milestones — typed API client for the LA attestation surface
// at /api/deal-flow/deals/{dealId}/pre-approval/*. Mutation hooks live in
// @/hooks/deals/usePreApproval.ts; this module is the raw transport.

import { api } from "./client";
import type {
  DealDetailDto,
  LinkPreApprovalDocumentRequest,
  PreApprovalApplicationDto,
  PreApprovalLetterDto,
  PreApprovalReadinessDto,
  PreApprovalSubmissionRequest,
  UpdatePreApprovalNotesRequest,
} from "@/types/deals";

const base = (dealId: string) => `/deal-flow/deals/${dealId}/pre-approval`;

export const preApprovalApplicationsApi = {
  list: (dealId: string) =>
    api.get<PreApprovalApplicationDto[]>(`${base(dealId)}/applications`),
  markGenerated: (dealId: string, id: string) =>
    api.post<PreApprovalApplicationDto>(`${base(dealId)}/applications/${id}/mark-generated`),
  markSent: (dealId: string, id: string) =>
    api.post<PreApprovalApplicationDto>(`${base(dealId)}/applications/${id}/mark-sent`),
  markReceived: (dealId: string, id: string) =>
    api.post<PreApprovalApplicationDto>(`${base(dealId)}/applications/${id}/mark-received`),
  updateNotes: (dealId: string, id: string, body: UpdatePreApprovalNotesRequest) =>
    api.patch<PreApprovalApplicationDto>(`${base(dealId)}/applications/${id}/notes`, body),
  linkDocument: (dealId: string, id: string, body: LinkPreApprovalDocumentRequest) =>
    api.post<PreApprovalApplicationDto>(`${base(dealId)}/applications/${id}/link-document`, body),
  unlinkDocument: (dealId: string, id: string) =>
    api.post<PreApprovalApplicationDto>(`${base(dealId)}/applications/${id}/unlink-document`),
};

export const preApprovalLettersApi = {
  list: (dealId: string) =>
    api.get<PreApprovalLetterDto[]>(`${base(dealId)}/letters`),
  markGenerated: (dealId: string, id: string) =>
    api.post<PreApprovalLetterDto>(`${base(dealId)}/letters/${id}/mark-generated`),
  markSent: (dealId: string, id: string) =>
    api.post<PreApprovalLetterDto>(`${base(dealId)}/letters/${id}/mark-sent`),
  markReceivedSigned: (dealId: string, id: string) =>
    api.post<PreApprovalLetterDto>(`${base(dealId)}/letters/${id}/mark-received-signed`),
  updateNotes: (dealId: string, id: string, body: UpdatePreApprovalNotesRequest) =>
    api.patch<PreApprovalLetterDto>(`${base(dealId)}/letters/${id}/notes`, body),
  linkDocument: (dealId: string, id: string, body: LinkPreApprovalDocumentRequest) =>
    api.post<PreApprovalLetterDto>(`${base(dealId)}/letters/${id}/link-document`, body),
  unlinkDocument: (dealId: string, id: string) =>
    api.post<PreApprovalLetterDto>(`${base(dealId)}/letters/${id}/unlink-document`),
};

export const preApprovalSubmissionApi = {
  readiness: (dealId: string) =>
    api.get<PreApprovalReadinessDto>(`${base(dealId)}/readiness`),
  submit: (dealId: string, body: PreApprovalSubmissionRequest) =>
    api.post<DealDetailDto>(`${base(dealId)}/submit`, body),
};
